import { Button, Flex } from "antd"
import { toaster } from "components/common/Toast"
import { serialize } from "object-to-formdata"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { getActionTranslations } from "utils/translations"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { routes } from "router"
import { updateTrip } from "store/trips"
import { spacings } from "themes/variables"
import styles from "./ListingActionButtons.module.css"
import { ListingCancelButton } from "./ListingCancelButton"
import { proNewListingMenuList } from "./proNewListingMenuList"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { useTabRoutes } from "./getTabRoute"


export const ListingActionButtons = ({ form, onFinish }) => {
  const { t } = useTranslation()
  const translations = getActionTranslations(t)
  const navigate = useNavigate()
  const [visible, setVisible] = useState()
  const [searchParams] = useSearchParams()
  const isPreviewMode = searchParams.get("readOnlyPreview")
  const backRoute = searchParams.get("route")
  const { id } = useParams()
  const { pathname } = useLocation()
  const isEdit = pathname.includes("edit")
  const tabId = pathname.split("/").filter(Boolean).pop()
  const currentTabIndex = useMemo(() => proNewListingMenuList.findIndex((tabItem) => tabItem.id === tabId), [tabId])
  const isFirstTab = currentTabIndex === 0
  const isLastTab = currentTabIndex === proNewListingMenuList.length - 1
  const dispatch = useDispatch()
  const { navigateToNextTab, navigateToPreviousTab } = useTabRoutes(backRoute, id, tabId, isEdit);
  const tripData = useSelector((state) => state?.trips)

  const formData = serialize({
    trip: {
      status: "published"
    }
  })

  const handleClickWithValidation = (navigateTo) => {
    if (onFinish instanceof Function) {
      onFinish(navigateTo)
    } else {
      navigateTo()
    }
  }

  const handleClickWithoutValidation = (navigateTo) => {
    navigateTo()
  }

  const publishBtnHandler = () => {
    dispatch(updateTrip(id, formData)).then(() => {
      toaster.success(t("experiences.successful_publish"))
      proNewListingMenuList.forEach((ele) => {
        ele.status = false
      })
      setTimeout(() => {
        navigate(routes?.proExperiencesPath())
      }, 300)
    })
  }

  const handlePreviewNavigation = () => {
    if (backRoute === "deactivated") {
      navigate(routes?.proExperiencePathDeactivatedPath())
    } else {
      navigate(routes?.proExperiencePathPublishedPath())
    }
  }

  const handleSaveAndUpdate = () => {
    onFinish(() => {
      if (backRoute === "deactivated") {
        navigate(routes?.proExperiencePathDeactivatedPath());
      } else if (backRoute === "published") {
        navigate(routes?.proExperiencePathPublishedPath());
      } else {
        navigate(routes?.proExperiencePathDraft());
      }
    });
  };

  const navigateToExperiencesPage = () => {
    navigate(routes?.proExperiencePathDraft())
  }

  return (
    <>
      <ListingCancelButton form={form} onFinish={onFinish} />
      <div className={styles.wrapper} style={{ flexWrap: "wrap" }}>
        <Flex justify="space-between" gap={10} className={styles.actionWrap}>
          {!isPreviewMode && !isFirstTab && (
            <Button
              className="pr-xl pl-xl previewBtn"
              color="primary"
              variant="outlined"
              size="large"
              onClick={() => handleClickWithoutValidation(navigateToPreviousTab)}
            >
              {t("global.previous")}
            </Button>
          )}
          {isEdit && !isPreviewMode && <Button
            className="pr-xl pl-xl previewBtn"
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => navigate(routes.proExperiencesPath())}
          >
            {t("global.cancel")}
          </Button>}
        </Flex>
        <Flex gap={spacings.S} className="save-wrapper" justify="flex-end">
          {!isPreviewMode && (
            <Button
              className="pr-xl pl-xl saveExitBtn"
              color="primary"
              variant={isEdit ? "solid" : "outlined"}
              size="large"
              onClick={() => isEdit ? handleSaveAndUpdate () : handleClickWithValidation(navigateToExperiencesPage)}
            >
              {isEdit ? t("listings.save_updates") : t("listings.save_draft_and_exit")}
            </Button>
          )}
          {isLastTab ? (
            <Button
              className="pr-xl pl-xl"
              color="primary"
              variant="solid"
              size="large"
              onClick={() => {
                if (!isPreviewMode) {
                  setVisible(true)
                } else {
                  handlePreviewNavigation()
                }
              }}
            >
              {isPreviewMode ? t("listings.preview_edit") : t("listings.save_publish")}
            </Button>
          ) : (
            <Button className="pr-xl pl-xl nextBtn" color="primary" variant="solid" size="large" onClick={() => handleClickWithValidation(navigateToNextTab)}
              disabled={isPreviewMode}>
              {t("global.next")}
            </Button>
          )}
        </Flex>
        <NewConfirmModal onClose={() => {
          setVisible(false)
        }}
          title={translations?.publish?.title}
          message={translations?.publish?.message}
          highlightText={tripData?.trip?.title}
          visible={visible}
          onConfirm={() => {
            publishBtnHandler()
          }}
          confirmLabel="Publish"
        />
      </div>
    </>
  )
}
